// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { OverflowTypography } from '@/components/overflow-typography';
import {
  BarChartRounded,
  InventoryOutlined,
  WarningAmberRounded,
} from '@/constants/hardfin-standard-icons';
import {
  getEffectiveAddress,
  getEffectiveLocationName,
} from '@/location/utilities/get-effective-third-party';
import { ProjectLineItem } from '@/project/api/types';
import { BillingPlanDrawerContainer } from '@/project/components/billing-plan-drawer-container';
import { ProjectLineItemGantt } from '@/project/components/project-line-item-gantt';
import { GanttItem } from '@/project/components/project-line-item-gantt-types';
import { TransactionTypeChip } from '@/project/components/transaction-type-chip';
import {
  getGroupColor,
  groupLineItems,
} from '@/project/utilities/group-line-items';
import { formatCalendarDateRangeDuration } from '@/utilities/format-calendar-date-range-duration';
import {
  Box,
  BoxProps,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTime, Interval } from 'luxon';
import { Fragment, useState } from 'react';

interface Props extends Pick<BoxProps, 'padding'> {
  lineItems: ProjectLineItem[];
  projectKey: string;
  projectStart: DateTime | null;
  projectEnd: DateTime | null;
  activeLineItemKey?: string;
  focusChargeKey?: string;
  onSelectLineItem?: (lineItemKey: string | null) => void;
  onFocusChargeComplete?: () => void;
}

function getItemName(lineItem: ProjectLineItem): string {
  return lineItem.item.sku
    ? `${lineItem.item.sku} (${lineItem.item.name})`
    : lineItem.item.name;
}

// 7-column grid shared by header and every card row. Fixed widths for Qty,
// Transaction, and Action ensure columns align across independent grid
// contexts (header vs. each Paper card). The outlined Paper border (1px) is
// matched by a transparent border on the header so left edges stay consistent.
const GRID_COLUMNS =
  'minmax(0, 3fr) 72px minmax(0, 2fr) minmax(0, 2fr) minmax(0, 2fr) 120px';

const HEADER_SX = {
  color: 'text.secondary' as const,
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  fontSize: '0.7rem',
  letterSpacing: '0.05em',
};

function ColumnHeaders() {
  return (
    <Box
      display="grid"
      gridTemplateColumns={GRID_COLUMNS}
      columnGap={2}
      pb={0.5}
      sx={{
        // Mirror the outlined Paper box model (1px border + p:2 padding) so
        // that fr columns resolve to identical widths in both contexts.
        border: '1px solid transparent',
        px: 2,
        py: 1,
      }}
    >
      <Typography sx={HEADER_SX}>Item</Typography>
      <Typography sx={{ ...HEADER_SX, textAlign: 'right' }}>
        Quantity
      </Typography>
      <Typography sx={HEADER_SX}>Location</Typography>
      <Typography sx={HEADER_SX}>Date range</Typography>
      <Typography sx={HEADER_SX}>Details</Typography>
      <Typography sx={HEADER_SX}>Transaction</Typography>
    </Box>
  );
}

interface DateRangeCellProps {
  startDate: DateTime | null;
  endDate: DateTime | null;
  duration: string | null;
  projectStart: DateTime | null;
  projectEnd: DateTime | null;
}

function DateRangeCell(cellProps: DateRangeCellProps) {
  const { startDate, endDate, duration, projectStart, projectEnd } = cellProps;

  const warnings: string[] = [];
  if (startDate && projectStart && startDate < projectStart) {
    warnings.push('Starts before the project');
  }
  if (startDate && projectEnd && startDate > projectEnd) {
    warnings.push('Starts after the project ends');
  }
  if (endDate && projectEnd && endDate > projectEnd) {
    warnings.push('Ends after the project');
  }
  if (endDate && projectStart && endDate < projectStart) {
    warnings.push('Ends before the project starts');
  }

  return (
    <Stack minWidth={0} spacing={1}>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography
          variant="body2"
          color={startDate && endDate ? 'text.primary' : 'text.secondary'}
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {startDate && endDate
            ? Interval.fromDateTimes(startDate, endDate).toLocaleString({
                dateStyle: 'medium',
              })
            : startDate
              ? `${startDate.toLocaleString({ dateStyle: 'medium' })} – ?`
              : endDate
                ? `? – ${endDate.toLocaleString({ dateStyle: 'medium' })}`
                : 'No dates set'}
        </Typography>
        {warnings.length > 0 && (
          <Tooltip
            title={warnings.map((w, i) => (
              <Fragment key={i}>
                {i > 0 && <br />}
                {w}
              </Fragment>
            ))}
            arrow
          >
            <WarningAmberRounded
              sx={{
                fontSize: '0.875rem',
                color: 'warning.main',
                flexShrink: 0,
              }}
            />
          </Tooltip>
        )}
      </Box>
      {duration && (
        <Typography variant="body2" color="text.secondary">
          {duration}
        </Typography>
      )}
    </Stack>
  );
}

export function ProjectLineItemsList(props: Props) {
  const [selectedGroupKey, setSelectedGroupKey] = useState<string | null>(null);

  const isDrawerOpen = props.activeLineItemKey != null;

  const handleOpenDrawer = (lineItem: ProjectLineItem) => {
    props.onSelectLineItem?.(lineItem.key);
  };

  const handleCloseDrawer = () => {
    props.onSelectLineItem?.(null);
  };

  if (props.lineItems.length === 0) {
    return (
      <Box padding={props.padding}>
        <Stack spacing={1} alignItems="center" paddingY={3}>
          <InventoryOutlined sx={{ fontSize: '2.5rem' }} color="action" />
          <Typography variant="subtitle2" color="text.secondary">
            No line items yet
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Add line items to get started on this project. Click Edit to set up
            your first line item.
          </Typography>
        </Stack>
      </Box>
    );
  }

  const groups = groupLineItems(props.lineItems);

  return (
    <Box padding={props.padding} overflow="auto">
      <ColumnHeaders />
      <Box display="flex" flexDirection="column" gap={1}>
        {groups.map((group, groupIdx) => {
          const isMultiItem = group.items.length > 1;
          const groupColor = getGroupColor(groupIdx);

          return (
            <Paper
              key={group.groupKey}
              variant="outlined"
              sx={{
                p: 2,
                pb: 1.5,
                ...(isMultiItem
                  ? { borderLeft: `3px solid ${groupColor}` }
                  : undefined),
              }}
            >
              {group.items.map((lineItem, itemIndex) => {
                const startDate = lineItem.startOn;
                const endDate = lineItem.endOn;
                const duration = formatCalendarDateRangeDuration(
                  startDate,
                  endDate
                );

                return (
                  <Fragment key={lineItem.id}>
                    <Box
                      display="grid"
                      gridTemplateColumns={GRID_COLUMNS}
                      columnGap={2}
                      alignItems="start"
                      sx={
                        itemIndex > 0
                          ? {
                              mt: 2,
                              pt: 2,
                              borderTop: '1px solid',
                              borderColor: 'divider',
                            }
                          : undefined
                      }
                    >
                      {/* Col 1: Item — SKU bold, name below in gray */}
                      <Stack minWidth={0} spacing={1}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <OverflowTypography
                            variant="body2"
                            fontWeight={600}
                            overflow="hidden"
                            whiteSpace="nowrap"
                            textOverflow="ellipsis"
                          >
                            {lineItem.item.sku}
                          </OverflowTypography>
                          {isMultiItem && itemIndex === 0 && (
                            <Tooltip title="View timeline" arrow>
                              <IconButton
                                size="small"
                                sx={{
                                  p: 0.25,
                                  ...(selectedGroupKey === group.groupKey
                                    ? { bgcolor: 'action.selected' }
                                    : undefined),
                                }}
                                onClick={() =>
                                  setSelectedGroupKey(
                                    selectedGroupKey === group.groupKey
                                      ? null
                                      : group.groupKey
                                  )
                                }
                              >
                                <BarChartRounded sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          overflow="hidden"
                          whiteSpace="nowrap"
                          textOverflow="ellipsis"
                        >
                          {lineItem.item.name}
                        </Typography>
                      </Stack>
                      {/* Col 2: Quantity */}
                      <Typography variant="body2" textAlign="right">
                        {(lineItem.quantity ?? 0).toLocaleString()}
                      </Typography>
                      {/* Col 3: Location — name, address below in gray */}
                      <Stack minWidth={0} spacing={1}>
                        <OverflowTypography
                          variant="body2"
                          color={
                            lineItem.location === null
                              ? 'text.secondary'
                              : 'text.primary'
                          }
                          overflow="hidden"
                          whiteSpace="nowrap"
                          textOverflow="ellipsis"
                        >
                          {lineItem.location
                            ? getEffectiveLocationName(lineItem.location)
                            : 'Not set'}
                        </OverflowTypography>
                        {lineItem.location &&
                          getEffectiveAddress(lineItem.location) && (
                            <OverflowTypography
                              variant="body2"
                              color="text.secondary"
                              overflow="hidden"
                              whiteSpace="nowrap"
                              textOverflow="ellipsis"
                            >
                              {getEffectiveAddress(lineItem.location)}
                            </OverflowTypography>
                          )}
                      </Stack>
                      {/* Col 4: Date range — range, duration below in gray */}
                      <DateRangeCell
                        startDate={startDate}
                        endDate={endDate}
                        duration={duration}
                        projectStart={props.projectStart}
                        projectEnd={props.projectEnd}
                      />
                      {/* Col 5: Details */}
                      <Typography
                        variant="body2"
                        color={
                          lineItem.details === null
                            ? 'text.secondary'
                            : 'text.primary'
                        }
                        overflow="hidden"
                        whiteSpace="nowrap"
                        textOverflow="ellipsis"
                      >
                        {lineItem.details ?? 'Not set'}
                      </Typography>
                      {/* Col 6: Transaction chip + Configure billing */}
                      <Stack spacing={1}>
                        <Box>
                          <TransactionTypeChip
                            transactionType={lineItem.transactionType}
                          />
                        </Box>
                        <Typography
                          variant="body2"
                          color="primary"
                          whiteSpace="nowrap"
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleOpenDrawer(lineItem)}
                        >
                          {lineItem.pricingMode === null
                            ? 'Add billing plan'
                            : 'Edit billing plan'}
                        </Typography>
                      </Stack>
                    </Box>
                  </Fragment>
                );
              })}
              {selectedGroupKey === group.groupKey && (
                <ProjectLineItemGantt
                  items={group.items.map(
                    (item): GanttItem => ({
                      id: item.id,
                      label: getItemName(item),
                      start: item.startOn,
                      end: item.endOn,
                      quantity: item.quantity,
                      unitPrice: item.unitPrice,
                    })
                  )}
                  groupColor={groupColor}
                  onClose={() => setSelectedGroupKey(null)}
                />
              )}
            </Paper>
          );
        })}
      </Box>
      <BillingPlanDrawerContainer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        projectKey={props.projectKey}
        lineItemKey={props.activeLineItemKey ?? null}
        focusChargeKey={props.focusChargeKey}
        onFocusChargeComplete={props.onFocusChargeComplete}
      />
    </Box>
  );
}
